/**
 * The edges of the window the app must not lay anything out in.
 *
 * Opened over the whole screen, the mini app gets the window telegram used to keep for
 * itself: the notch cuts into the top of the page and telegram paints its own close and
 * menu buttons over it. Whatever the app puts at the top of the document ends up under
 * those - visible, but impossible to press, because the touch never reaches the page.
 *
 * The two are reported separately and both apply at once, so the offset to keep clear is
 * their sum: safeAreaInset is the device and contentSafeAreaInset is the telegram header.
 *
 * Both arrived in Bot API 8.0, together with fullscreen mode itself - a client old enough
 * to report neither cannot open the app that way either. There the values stay 0 and the
 * stylesheet falls back to env(safe-area-inset-*), see styles/global.css.
 */

import { getWebApp, type Insets } from "./sdk";

const SIDES = ["top", "bottom", "left", "right"] as const;

/** The events after which the numbers can be different ones. */
const EVENTS = ["safeAreaChanged", "contentSafeAreaChanged", "fullscreenChanged"] as const;

function applyInsets(): void {
  const webApp = getWebApp();
  const root = document.documentElement;

  for (const side of SIDES) {
    const total = sizeOf(webApp?.safeAreaInset, side) + sizeOf(webApp?.contentSafeAreaInset, side);
    root.style.setProperty(`--app-inset-${side}`, `${total}px`);
  }
}

/** One side of an inset telegram may not have reported at all, as a usable number. */
function sizeOf(insets: Insets | undefined, side: (typeof SIDES)[number]): number {
  const value = insets?.[side];
  return typeof value === "number" && Number.isFinite(value) ? Math.max(value, 0) : 0;
}

/** Follow the insets for as long as the app lives: rotating the phone changes them. */
export function watchInsets(): () => void {
  applyInsets();

  const webApp = getWebApp();
  if (!webApp) {
    return () => {};
  }

  for (const event of EVENTS) {
    webApp.onEvent(event, applyInsets);
  }
  return () => {
    for (const event of EVENTS) {
      webApp.offEvent(event, applyInsets);
    }
  };
}
